@let hasInvoiceCreatePermission =
  can(RoleAction.Create, RolePermissionSubject.Invoice);
@let forbiddenMessage = 'general.forbidden_action' | translate;
@let hasShiftAttachmentsFeatureFlagEnabled =
  'portal-show-shift-attachments-on-invoice' | featureFlagPure | async;
@let selectedAttachmentsSizeInMb =
  invoiceAttachmentsHelperService.totalSizeOfSelectedAttachmentsInBytesSignal()
    | bytesToMb;

@let isInvoiceEditButtonVisible =
  invoiceForm.value.status !== null &&
  invoiceForm.value.status !== 'draft' &&
  invoiceForm.value.status !== 'cancelled' &&
  invoiceForm.value.status !== 'paid';

<wilson-page-frame-v3 [stickyHeader]="true">
  <ng-container headerContent>
    <div class="d-flex align-items-center justify-content-center">
      <span class="toolbar-title">
        @if (invoiceForm.value.status) {
          {{ 'page.invoicing.status.' + status | translate }}
        } @else {
          {{ 'page.invoicing.status.draft' | translate }}
        }
      </span>

      @if (
        !(
          (invoiceForm.value.status === 'draft' ||
            invoiceForm.value.status === null) &&
          canEditInvoice
        ) && status !== 'cancelled'
      ) {
        <span class="divider-line"></span>
        <ng-container>
          <wilson-invoice-status-select
            class="status-select"
            [ngModel]="status"
            (ngModelChange)="onStatusChange($event)"
            [allowClear]="false"
            [statuses]="filteredStatuses"
            [hasPermission]="hasInvoiceCreatePermission"
          ></wilson-invoice-status-select>
        </ng-container>
      }
      @if (invoiceForm.value.lexofficeId) {
        <a
          class="lexoffice-link ms-2"
          href="https://app.lexoffice.de/vouchers#!/VoucherView/Invoice/{{
            invoiceForm.value.lexofficeId
          }}"
          target="_blank"
        >
          <div class="d-flex align-items-center">
            <fa-icon [icon]="faLink" class="me-2"></fa-icon>
            <img
              class="lex-office-logo me-1"
              [src]="lexOfficeLogo"
              alt="LexOffice Logo"
            />
            {{ 'page.invoicing.lexOffice' | translate }}
          </div>
        </a>
        @if (
          invoiceForm.value.status !== 'cancelled' &&
          invoiceForm.value.status !== 'paid'
        ) {
          <fa-icon
            class="ms-2 unlink-icon"
            (click)="unlinkLexOffice()"
            [icon]="faUnlink"
          ></fa-icon>
        }
      }
    </div>
    <div class="d-flex">
      @if (
        invoiceForm.value.status !== null &&
        invoiceForm.value.status !== 'draft' &&
        invoiceForm.value.status !== 'cancelled'
      ) {
        @if ('portal-invoice-management' | featureFlagPure | async) {
          <button
            [disabled]="!hasInvoiceCreatePermission || invoiceForm.invalid"
            nz-button
            nzType="default"
            class="text-dark fw-normal me-3"
            (click)="cancel()"
            nz-tooltip
            [nzTooltipTitle]="
              !hasInvoiceCreatePermission ? forbiddenMessage : ''
            "
          >
            @if (isSavingInvoice) {
              <span
                class="spinner-border spinner-border-sm me-1"
                role="saveAndFinalize"
                aria-hidden="true"
              ></span>
            }
            {{ 'page.invoice.cancel_invoice' | translate }}
          </button>
        }
      }
      <button nz-button nz-dropdown [nzDropdownMenu]="menu4">
        {{ 'page.invoicing.export' | translate }}
        <span nz-icon nzType="down"></span>
      </button>
      <nz-dropdown-menu #menu4="nzDropdownMenu">
        <ul nz-menu>
          @if (
            !canEditInvoice && ('portal-export-e-invoice' | featureFlagPure)
          ) {
            <li nz-menu-item (click)="exportEinvoice()">
              {{ 'page.invoicing.export_e_invoice' | translate }}
            </li>
          }
          <li
            nz-menu-item
            [nzDisabled]="invoiceForm.invalid"
            (click)="createCsv()"
          >
            {{ 'page.invoicing.export_csv' | translate }}
          </li>
          @if (
            !canEditInvoice && ('porta-export-lex-office' | featureFlagPure)
          ) {
            <li
              [nzDisabled]="invoiceForm.value.lexofficeId"
              nz-menu-item
              (click)="exportLex()"
            >
              {{ 'page.invoicing.export_lex_office' | translate }}
            </li>
          }
          @if (!canEditInvoice && ('porta-export-datev' | featureFlagPure)) {
            <li nz-menu-item (click)="exportDatev()">
              {{ 'page.invoicing.export_datev' | translate }}
            </li>
          }
        </ul>
      </nz-dropdown-menu>
      @if (isInvoiceEditButtonVisible) {
        @if (
          ('portal-invoice-management' | featureFlagPure | async) &&
          ('portal-edit-invoice-button' | featureFlagPure | async)
        ) {
          <button
            [disabled]="invoiceForm.invalid"
            nz-button
            nzType="default"
            class="text-dark fw-normal ms-3"
            (click)="editAgainInvoice()"
          >
            @if (isSavingInvoice) {
              <span
                class="spinner-border spinner-border-sm me-1"
                role="saveAndFinalize"
                aria-hidden="true"
              ></span>
            }
            {{ 'page.invoice.edit_again' | translate }}
          </button>
        }
      }
      @if (
        invoiceForm.value.status === 'draft' ||
        invoiceForm.value.status === null ||
        invoiceForm.value.canEditInvoice
      ) {
        <button
          nz-button
          nzType="primary"
          class="text-dark fw-normal ms-3"
          (click)="generatePdf()"
          [disabled]="
            isGeneratingPDF || invoiceForm.invalid || !invoiceForm.dirty
          "
        >
          @if (isGeneratingPDF) {
            <span
              class="spinner-border spinner-border-sm me-1"
              role="isGeneratingPDF"
              aria-hidden="true"
            ></span>
          }
          {{ 'page.invoicing.generate_pdf' | translate }}
        </button>
        @if ('portal-invoice-management' | featureFlagPure | async) {
          <button
            [disabled]="!hasInvoiceCreatePermission || invoiceForm.invalid"
            nz-button
            class="text-dark fw-normal ms-3"
            (click)="saveInvoice()"
            nz-tooltip
            [nzTooltipTitle]="
              !hasInvoiceCreatePermission ? forbiddenMessage : ''
            "
          >
            @if (isSavingInvoice) {
              <span
                class="spinner-border spinner-border-sm me-1"
                role="isSavingInvoice"
                aria-hidden="true"
              ></span>
            }
            {{ 'page.invoice.save_as_draft' | translate }}
          </button>
        }
        @if (invoiceForm.value.status !== null && invoiceId() !== null) {
          @if ('portal-invoice-management' | featureFlagPure | async) {
            <button
              [disabled]="
                !hasInvoiceCreatePermission ||
                invoiceForm.invalid ||
                manualInvoiceNumber.isFinalizeBlocked()
              "
              nz-button
              nzType="primary"
              class="text-dark fw-normal ms-3"
              (click)="finalizeAndSaveInvoice()"
              nz-tooltip
              [nzTooltipTitle]="
                !hasInvoiceCreatePermission
                  ? forbiddenMessage
                  : (manualInvoiceNumber.finalizeBlockedReason() ?? ''
                    | translate)
              "
            >
              @if (isSavingInvoice) {
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="saveAndFinalize"
                  aria-hidden="true"
                ></span>
              }
              {{ 'page.invoice.finalize_and_save' | translate }}
            </button>
          }
        }
      }
    </div>
  </ng-container>
  <ng-container mainContent>
    <div
      class="d-flex justify-content-between"
      [ngClass]="{
        'mainContent--finalized': !canEditInvoice,
      }"
    >
      @if (canEditInvoice) {
        <div class="flex-grow-1 background form-container pt-2">
          <form [formGroup]="invoiceForm">
            <div class="row m-auto">
              <div class="col-5">
                <wilson-customer-information-form
                  [canEditInvoice]="canEditInvoice"
                  [form]="invoiceForm.controls.customerDetails"
                ></wilson-customer-information-form>
              </div>
              <div class="col-7">
                <wilson-invoice-details-form
                  [canEditInvoice]="canEditInvoice"
                  [form]="invoiceForm.controls.invoiceDetails"
                  [invoiceId]="invoiceId()"
                  [currentStatus]="invoiceForm.value.status"
                  [billingSetting]="billingSetting()"
                ></wilson-invoice-details-form>
              </div>
              <div class="col-12">
                <wilson-invoice-header-form
                  [canEditInvoice]="canEditInvoice"
                  [form]="invoiceForm.controls.invoiceHeader"
                ></wilson-invoice-header-form>
              </div>
              <div class="col-12">
                <wilson-items-to-invoice
                  [canEditInvoice]="canEditInvoice"
                  [form]="invoiceForm.controls.invoiceItems"
                  [selectedCurrency]="selectedCurrency"
                ></wilson-items-to-invoice>
                @let advancePaymentAmount =
                  invoiceForm.value.advancePayment ?? 0;
                @let advancePaymentLocalizedCurrency =
                  advancePaymentAmount
                    | localizedNumber: 'currency' : selectedCurrency;

                <div class="section advance-payment">
                  <wilson-invoice-total-entries
                    [editMode]="canEditInvoice"
                    [label]="'page.invoicing.net_sum' | translate"
                    [value]="
                      invoiceTotals.netSum
                        | localizedNumber: 'currency' : selectedCurrency
                    "
                  ></wilson-invoice-total-entries>
                  <wilson-invoice-total-entries
                    [editMode]="canEditInvoice"
                    [label]="'page.invoicing.vat_sum' | translate"
                    [value]="
                      invoiceTotals.vatSum
                        | localizedNumber: 'currency' : selectedCurrency
                    "
                  ></wilson-invoice-total-entries>
                  <wilson-invoice-total-entries
                    [editMode]="canEditInvoice"
                    [label]="'page.invoicing.gross_sum' | translate"
                    [value]="
                      invoiceTotals.totalSum
                        | localizedNumber: 'currency' : selectedCurrency
                    "
                  ></wilson-invoice-total-entries>
                  @if (canEditInvoice || advancePaymentAmount > 0) {
                    <div class="row d-flex align-items-center">
                      <div [class]="canEditInvoice ? 'col-7' : 'col-8'">
                        <span class="font">
                          {{ 'page.invoicing.advance_payment' | translate }}
                        </span>
                      </div>
                      <div class="col-2 d-flex justify-content-end positioning">
                        @if (!canEditInvoice) {
                          <span class="font">
                            {{ advancePaymentLocalizedCurrency }}
                          </span>
                        } @else {
                          <wilson-input
                            type="currency"
                            [min]="0"
                            [currency]="CurrencySymbol[selectedCurrency]"
                            formControlName="advancePayment"
                          />
                        }
                      </div>
                      <div class="col-2 d-flex justify-content-end">
                        <span class="font advance-payment-sum">
                          {{ advancePaymentLocalizedCurrency }}
                        </span>
                      </div>
                      @if (canEditInvoice) {
                        <div class="col-1"></div>
                      }
                    </div>
                  }
                  <wilson-invoice-total-entries
                    [editMode]="canEditInvoice"
                    [label]="'page.invoicing.payment_sum' | translate"
                    [value]="
                      invoiceTotals.amountDueForPayment
                        | localizedNumber: 'currency' : selectedCurrency
                    "
                  ></wilson-invoice-total-entries>
                </div>
              </div>
              <ng-container
                *ngTemplateOutlet="invoiceAttachmentsTemplate"
              ></ng-container>
              <div class="col-12 mt-4">
                <wilson-invoice-footer-form
                  [canEditInvoice]="canEditInvoice"
                  [form]="invoiceForm.controls.invoiceFooter"
                ></wilson-invoice-footer-form>
              </div>
            </div>
            <ng-template #sectionHeader>
              <span class="font fw-normal">
                {{ 'page.invoicing.invoice_information' | translate }}
              </span>
            </ng-template>
          </form>
        </div>
      } @else {
        <ng-container
          *ngTemplateOutlet="finalizedInvoiceFormContainer"
        ></ng-container>
      }
      <div
        class="flex-shrink-0 background"
        [ngClass]="{
          'pdf-preview-container': canEditInvoice,
          'pdf-preview-container--finalized': !canEditInvoice,
        }"
      >
        <wilson-pdf-preview
          [pdfURL]="pdfURL"
          [showAll]="!canEditInvoice"
          [fullHeight]="!canEditInvoice"
        >
          @if (hasShiftAttachmentsFeatureFlagEnabled) {
            <nz-space-compact>
              <button
                nz-button
                nzType="default"
                class="text-dark fw-normal"
                (click)="downloadInvoicePdf()"
                [disabled]="isDownloadingPDF || invoiceForm.invalid"
              >
                @if (isDownloadingPDF) {
                  <span
                    class="spinner-border spinner-border-sm me-1"
                    aria-hidden="true"
                  ></span>
                }
                {{ 'page.invoicing.create_pdf' | translate }}
              </button>
              <button
                nz-button
                nz-dropdown
                [nzDropdownMenu]="menu"
                nzPlacement="bottomRight"
              >
                <nz-icon nzType="down" />
              </button>
              <nz-dropdown-menu #menu="nzDropdownMenu">
                <ul nz-menu>
                  @let isDownloadMergedPdfDisabled =
                    isDownloadingPDF ||
                    invoiceForm.invalid ||
                    selectedAttachmentsSizeInMb === 0;
                  <li
                    nz-menu-item
                    [nzDisabled]="isDownloadMergedPdfDisabled"
                    nz-tooltip
                    [nzTooltipTitle]="
                      selectedAttachmentsSizeInMb === 0
                        ? ('page.invoicing.invoice_attachments.no_attachments_selected'
                          | translate)
                        : ''
                    "
                    (click)="
                      !isDownloadMergedPdfDisabled
                        ? downloadMergedPdf(progressTemplate)
                        : null
                    "
                  >
                    {{
                      'page.invoicing.create_pdf_with_attachments' | translate
                    }}
                  </li>
                </ul>
              </nz-dropdown-menu>
            </nz-space-compact>
          } @else {
            <button
              nz-button
              nzType="default"
              class="text-dark fw-normal"
              (click)="downloadInvoicePdf()"
              [disabled]="isDownloadingPDF || invoiceForm.invalid"
            >
              @if (isDownloadingPDF) {
                <span
                  class="spinner-border spinner-border-sm me-1"
                  aria-hidden="true"
                ></span>
              }
              {{ 'page.invoicing.create_pdf' | translate }}
            </button>
          }
        </wilson-pdf-preview>
      </div>
    </div>
  </ng-container>
</wilson-page-frame-v3>

<ng-template #progressTemplate let-data="data">
  <wilson-invoice-download-progress
    [data]="data"
  ></wilson-invoice-download-progress>
</ng-template>

<ng-template #ciiXmlDownloadProgressTemplate let-data="data">
  @let status = $safeNavigationMigration(data?.status$) | async;
  <div class="d-flex align-items-center gap-2">
    @if (status !== 'ready') {
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
    }
    <span>
      @if (status === 'ready') {
        {{ 'page.invoicing.cii_xml_download.status_ready' | translate }}
      } @else {
        {{ 'page.invoicing.cii_xml_download.status_generating' | translate }}
      }
    </span>
  </div>
</ng-template>

<ng-template #invoiceAttachmentsTemplate>
  @defer {
    @if (hasShiftAttachmentsFeatureFlagEnabled && shiftAttachments) {
      <div class="col-12 mt-4">
        <wilson-invoice-attachments
          [shiftAttachments]="shiftAttachments"
          [invoiceAttachments]="invoiceAttachments"
          [areCheckboxesDisabled]="isInvoiceEditButtonVisible"
          [disablePreSelection]="disablePreSelectionOfShiftReports"
          (refreshShiftAttachments)="refreshShiftAttachments.emit()"
          (generatePdf)="generatePdf()"
          [compact]="!canEditInvoice"
        ></wilson-invoice-attachments>
      </div>
    }
  }
</ng-template>

<ng-template #finalizedInvoiceFormContainer>
  <div class="pe-3 background form-container--finalized">
    <div nz-flex nzDirection="column" nzGap="small">
      <div class="mb-2">
        <span>
          {{ 'page.invoicing.cant_edit_invoice.title' | translate }}
        </span>
      </div>
      <div nz-flex nzGap="small">
        <span class="cant-edit-invoice-description">
          {{ 'page.invoicing.cant_edit_invoice.description' | translate }}
        </span>
        <fa-icon [icon]="faCircleInfo" size="xs" class="support-icon"></fa-icon>
      </div>
      @if (
        manualInvoiceNumber.isManualMode() &&
        hasInvoiceCreatePermission &&
        invoiceForm.value.status !== 'cancelled'
      ) {
        <div class="manual-invoice-number">
          <wilson-manual-invoice-number-field
            [control]="
              invoiceForm.controls.invoiceDetails.controls.invoiceNumber
            "
            [showSaveCorrection]="true"
            [isSaving]="isSavingInvoice"
            (saveCorrection)="saveNumberCorrection()"
          ></wilson-manual-invoice-number-field>
        </div>
      }
      <ng-container
        *ngTemplateOutlet="invoiceAttachmentsTemplate"
      ></ng-container>
    </div>
  </div>
</ng-template>
